import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { trackPageView } from "@/lib/pixel";
import { trackGaPageView } from "@/lib/ga";
import Home from "@/pages/Home";
import Sell from "@/pages/Sell";
import Buy from "@/pages/Buy";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminInventory from "@/pages/admin/Inventory";
import AdminConsignors from "@/pages/admin/Consignors";
import AdminBuyers from "@/pages/admin/Buyers";
import AdminPartners from "@/pages/admin/Partners";
import AdminPartnerApplications from "@/pages/admin/PartnerApplications";
import AdminAffiliateApplications from "@/pages/admin/AffiliateApplications";
import CatalogUnits from "@/pages/admin/CatalogUnits";
import CatalogInterests from "@/pages/admin/CatalogInterests";
import CatalogGrid from "@/pages/catalog/CatalogGrid";
import CatalogDetail from "@/pages/catalog/CatalogDetail";
import AdvisorLogin from "@/pages/asesor/AdvisorLogin";
import AdvisorSetPassword from "@/pages/asesor/AdvisorSetPassword";
import AdvisorForgot from "@/pages/asesor/AdvisorForgot";
import AdvisorPortal from "@/pages/asesor/AdvisorPortal";
import { advisorToken } from "@/lib/advisorApi";

function AdvisorRoute({ children }) {
  if (!advisorToken.get()) return <Navigate to="/asesor/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Fires Meta Pixel PageView on SPA route changes. Skips the very first mount
 * because the inline snippet in /public/index.html already fires PageView on
 * hard page loads. Never fires on /admin/* routes (the helper checks itself).
 */
function MetaPixelTracker() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

/**
 * Fires GA4 page_view on SPA route changes. Skips the very first mount
 * because the inline snippet in /public/index.html already fires the
 * initial page_view via `gtag('config', ...)` on hard page loads. Never
 * fires on /admin/* routes (the helper checks itself).
 */
function GA4Tracker() {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    trackGaPageView(pathname);
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white text-[#3E5641]" data-testid="auth-loading">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <MetaPixelTracker />
          <GA4Tracker />
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/vender" element={<Sell />} />
            <Route path="/comprar" element={<Buy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/asesor/login" element={<AdvisorLogin />} />
            <Route path="/asesor/definir-contrasena" element={<AdvisorSetPassword />} />
            <Route path="/asesor/olvide-contrasena" element={<AdvisorForgot />} />
            <Route path="/asesor" element={<AdvisorRoute><AdvisorPortal /></AdvisorRoute>} />
            <Route path="/asesor/clientes/:clienteId" element={<AdvisorRoute><AdvisorPortal /></AdvisorRoute>} />
            <Route path="/inventario/:category" element={<CatalogGrid />} />
            <Route path="/inventario/:category/:id" element={<CatalogDetail />} />
            <Route
              path="/admin"
              element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}
            >
              <Route index element={<Navigate to="inventario" replace />} />
              <Route path="inventario" element={<AdminInventory />} />
              <Route path="consignantes" element={<AdminConsignors />} />
              <Route path="compradores" element={<AdminBuyers />} />
              <Route path="socios" element={<AdminPartners />} />
              <Route path="solicitudes-socios" element={<AdminPartnerApplications />} />
              <Route path="solicitudes-afiliados" element={<AdminAffiliateApplications />} />
              <Route path="inventario-seminuevos" element={<CatalogUnits category="seminuevo" moduleNumber="07" />} />
              <Route path="inventario-nuevos" element={<CatalogUnits category="nuevo" moduleNumber="09" />} />
              <Route path="inventario-promociones" element={<CatalogUnits category="promocion" moduleNumber="10" />} />
              <Route path="interesados-inventario" element={<CatalogInterests />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
